import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Server-side rendering a D3 chart with React 16",
  "description": "",
  "published": "2017-09-28T00:00:00.000Z",
  "redirect_from": ["/blog/server-side-rendering-d3-chart-react-16/swizec/7824"],
  "categories": "react, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`The Flash of Doom is one of the most annoying issues with D3 charts. You load the page, then you load the data, `}<em parentName="p">{`then`}</em>{` you render your chart.`}</p>
    <p>{`Everyone's staring at a blank screen while the data loads.`}</p>
    <p>{`With React 16, we can fix that using server-side rendering and the new `}<inlineCode parentName="p">{`ReactDOM.hydrate`}</inlineCode>{` feature. The idea is to serve a fully rendered chart in the initial HTML payload, then take over with JavaScript and become a normal webapp.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/09/server-side-d3.gif",
        "alt": null
      }}></img></p>
    <p>{`Pay close attention to that gif. The flashing red shows repaints.`}</p>
    <p>{`When I reload the page, the whole thing re-renders and shows a line chart. Axes show up after a little while, and they're the only part of the page that re-renders.`}</p>
    <p>{`Here's a slower version without the flashing.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/09/server-side-d3-no-flash.gif",
        "alt": null
      }}></img></p>
    <p>{`Here's what you're seeing 👇`}</p>
    <ol>
      <li parentName="ol">{`You hit reload`}</li>
      <li parentName="ol">{`Server reads `}<inlineCode parentName="li">{`index.html`}</inlineCode>{` from `}<a parentName="li" {...{
          "href": "https://github.com/facebookincubator/create-react-app"
        }}>{`create-react-app`}</a></li>
      <li parentName="ol">{`Server reads local CSV file with data`}</li>
      <li parentName="ol">{`Server renders `}<inlineCode parentName="li">{`<App />`}</inlineCode>{` into root HTML element`}</li>
      <li parentName="ol">{`Server sends the full `}<inlineCode parentName="li">{`index.html`}</inlineCode>{` to your browser`}</li>
      <li parentName="ol">{`Browser shows HTML with the chart`}</li>
      <li parentName="ol">{`Browser loads remote CSV file with data`}</li>
      <li parentName="ol">{`Browser runs `}<inlineCode parentName="li">{`ReactDOM.hydrate()`}</inlineCode>{` to render `}<inlineCode parentName="li">{`<App />`}</inlineCode></li>
      <li parentName="ol"><inlineCode parentName="li">{`<App />`}</inlineCode>{` takes over the DOM and becomes a normal webapp`}</li>
    </ol>
    <p>{`Some parts of this are efficient.`}</p>
    <p><inlineCode parentName="p">{`ReactDOM.hydrate`}</inlineCode>{` avoids re-rendering parts of the DOM that were already rendered by your server. In our case, that's everything except the axes.`}</p>
    <p>{`Some parts of this are inefficient.`}</p>
    <p>{`The server shouldn't need to read the CSV and HTML files on every request. You could do that on startup and save the strings in a variable. They're static.`}</p>
    <p>{`We're also rendering `}<inlineCode parentName="p">{`<App />`}</inlineCode>{` and running all of our React code twice. But this doesn't need to happen every time on the server. Cache that stuff!`}</p>
    <h2 {...{
      "id": "how-to"
    }}>{`How to`}</h2>
    <p>{`You can try it out for yourself `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/server-side-d3-poc"
      }}>{`on Github`}</a>{`.`}</p>
    <p>{`The hardest part was adding a small Express server to `}<inlineCode parentName="p">{`create-react-app`}</inlineCode>{` that supports `}<inlineCode parentName="p">{`import`}</inlineCode>{` and React syntax. I couldn't figure it out on my own, so I used `}<a parentName="p" {...{
        "href": "https://medium.com/@benlu/ssr-with-create-react-app-v2-1b8b520681d9"
      }}>{`Ben Lu's`}</a>{` amazing `}<a parentName="p" {...{
        "href": "https://medium.com/@benlu/ssr-with-create-react-app-v2-1b8b520681d9"
      }}>{`SSR with create-react-app`}</a>{` guide.`}</p>
    <p>{`The main trick seems to be installing `}<inlineCode parentName="p">{`ignore-styles`}</inlineCode>{` so your server doesn't error out when `}<inlineCode parentName="p">{`require`}</inlineCode>{`-ing CSS files (no webpack), and figuring out just the right incantation of Express setup to get the server working. I tried simplifying his setup, but it didn't work.`}</p>
    <p>{`In the end, your best bet might be to just copy his `}<inlineCode parentName="p">{`server/`}</inlineCode>{` directory and go from there. Adapt `}<inlineCode parentName="p">{`server/universal.js`}</inlineCode>{` to render your `}<inlineCode parentName="p">{`<App />`}</inlineCode>{` instead of his ReactRouter stuff.`}</p>
    <p>{`You also have to go into `}<inlineCode parentName="p">{`public/index.html`}</inlineCode>{` and add `}<inlineCode parentName="p">{`{{SSR}}`}</inlineCode>{` to the body of `}<inlineCode parentName="p">{`<div id="root">`}</inlineCode>{`. The server looks for `}<inlineCode parentName="p">{`{{SSR}}`}</inlineCode>{` and replaces it with the rendered HTML from your React app.`}</p>
    <p>{`You can see my server-side code that reads a CSV file and renders the chart into a string `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/server-side-d3-poc/blob/master/server/universal.js"
      }}>{`on Github`}</a>{`.`}</p>
    <h2 {...{
      "id": "adapting-your-react-d3-app-to-server-side"
    }}>{`Adapting your React D3 app to server-side`}</h2>
    <p>{`Adjusting to server-side rendering required a small mind shift in the way I built my chart. Usually, I like to use `}<inlineCode parentName="p">{`componentWillMount`}</inlineCode>{` in the `}<inlineCode parentName="p">{`<App />`}</inlineCode>{` component to load data. Until data loads, the app renders a `}<inlineCode parentName="p">{`null`}</inlineCode>{`; after that, it returns a chart component.`}</p>
    <p>{`This makes apps easy to build and avoids issues with undefined data when rendering.`}</p>
    <p>{`But it throws away all benefits of server-side rendering. With the `}<inlineCode parentName="p">{`componentWillMount`}</inlineCode>{` approach, you're loading the fully rendered chart, replacing it with an empty component, then re-rendering it once data loads on the client.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "890px",
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
      `}<a parentName="span" {...{
          "className": "gatsby-resp-image-link",
          "href": "/static/5b52962d605c2ca5582ca608d2bd6c62/72e01/statuejpgaa03i3.jpg",
          "style": {
            "display": "block"
          },
          "target": "_blank",
          "rel": "noopener"
        }}>{`
    `}<span parentName="a" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "66.81614349775785%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'267\\'%20viewBox=\\'0%200%20400%20267\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M0%20134v133h102a1984%201984%200%2000101-2l-9-2-7-1-3-7-4-6c-7%200-14-16-17-37-2-14-4-21-9-36-4-11-3-12%201-11l4-1c1-3%204-2%205%201%203%206%206%206%207%201%201-6%203-7%205-4%201%202%202%203%201%205l2%205%202%204%202%203%201%203c0%203%203%206%207%206l4%202%202%203c2%201%203%202%204%207l3%208c2%203%201%2017-1%2020v6l-2%206-1%202-1%204%201%205c0%202%202%201%202-1%201-3%206-3%209-1%202%203%202%2012-1%2015l-2%203h43l-4-4c-6-5-13-19-13-23l-4-12a158%20158%200%2001-8-17l-2-1-5-5c-5-4-8-9-11-15-2-5-3-6-1-12l1-19c0-10%200-13%202-17%203-5%203-19%202-23-2-3-1-10%201-12l2-5%201-4%201-2c0-1%200-2-1-1l-1-6v-3c-2%202-2%202-2%200l1-4h3l1%202v-2l-1-3h18l-2-3-8-2c-3%201-5%200-6-1h-3l-2%201-4-6c-4-5-4-5-3-7%202-2%203-4%202-7-2-5%200-5%206-4l15%203a436%20436%200%200166%2014l10%207%2016%2014v3c3%203%201%204-3%201l-3-1-1-1c-1-2-8-7-11-7v12l2%2019c1%2014%202%2018%207%2016%201%200%202%200%202%202l3%207%203%207%201%205v8c-3%208-3%2010%201%2010%203-1%203%200%204%204a938%20938%200%200011%2054c2%200%202%207%201%2010l-1%2016-1%2014h68V0H0v134M126%203c-4%201-9%204-15%209l-7%204-4%205-2%203c-1%200-11%2010-12%2013-5%208-5%2029%200%2045l1%207%201%203%201%201v3c1%202%202%203%203%202%202-1%202-1%200-2-1-2-1-2%201-2%202-1%202%200%203%206%200%208%202%2010%204%207%202-2%202-3%202-8V88c1-6%201-11-2-11l-3-1c-2-3%201-3%204-1s9%203%208%200c-1-1%200-1%202-1l14-1c-1-1%203-2%208-2%203%201%204%201%205-1l3-2v-2l-1-4c0-2-1-2-7-1-3%201-2-2%202-6%203-2%204-5%204-6l1-3%201%202c0%204%202%202%204-4%202-3%202-6%202-9-1-4%200-4%202%200%203%204%203%2010%201%2010-2-1-3%203-1%206%201%202%200%204-4%206l-2%203%201%201h3c2-2%203-2%204-1h2v-2c2%201%202%200%202-2%200-1%200-2%201-1l1%202%201%204%201%205c2%201%204-2%204-5l1-4c1-1%201-2-1-6-2-5-3-5-3-3l-1%203v-3l1-5%201-2c-2%200-2-1-2-4%200-6%202-6%203%200%201%204%203%204%203-1l1-3%201-1c0-2%204-5%205-5%201%201%205-3%204-5%200-2%201-1%204%202%204%205%205%206%203%206v1c2%202%202%201%202-3-1-4-6-10-11-12l-7-4c-4-3-11-5-19-7l-8-2h-14m52%2040c-4%200-6%201-6%204-1%202-1%202%201%201%206-2%2011%200%2011%204l1%207v6h3c2-2%201-4-2-14-2-8-3-9-8-8m-57%2037l-4%207a681%20681%200%2001-4%2023c1%207%200%2013-2%2013l1%202%204%204c2%203%207%204%2018%205%205%201%206%201%209-2l3-4%203-7%202-7%204-5%203-6%202-2c1-1%203-7%202-8-3-1-15%2014-18%2022-2%207-7%2012-11%2011-2%200-2%200%200-5l1-7v-3l1-3c1-1%201-2%202-1l2-2c1-4%204-11%207-15%206-9%201-12-8-5-2%202-2%202-2-1l-1-3v6c0%206-1%209-3%2013l-4%2013c-1%207-1%209-4%2011-4%204-5%203-5-6%200-6-1-8-2-8v-1l2-1c1%201%202-2%203-9l4-11c5-11%202-15-5-8m41-2c-2%200-2%201-2%204v6c-1%203%201%205%203%203%200-2%201-2%201-1v3c1%203%203%204%203%201V80c-1-3-2-3-5-2m156%2021l-2%204%202%205%205%209%202%204%201-2%203-3v-7c0-2%200-2-1-1s-1%201-1-1l-2-2-1-1v-1l-1-3-1-3c-1-2-2-2-4%202m-129%202l-1%202c-1%201-2%2018-1%2019l4-3%204-4%201%203c1%202%201%202%202-2l1-2c0%203%202%204%202%201l2-4c2-2%201-4-1-5l-3-1h-2l-4%201c-3%200-3%200-2-2l-1-3h-1m141%2028l2%208c1%208%203%2011%209%2023%202%204%204%205%204%202l1-2v-10l-1-3-1-3-1-4v-4l-2-2c-1-3-6-5-8-4l-1-1-1-1-1%201m-67%2055c-3%200-6%202-9%204l-9%204-5%202c-1%203%2015%205%2021%203l4-1%204-3c3-3%203-3%202-6l-2-4-6%201\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/5b52962d605c2ca5582ca608d2bd6c62/ca0a1/statuejpgaa03i3.webp 223w", "/static/5b52962d605c2ca5582ca608d2bd6c62/75680/statuejpgaa03i3.webp 445w", "/static/5b52962d605c2ca5582ca608d2bd6c62/8d1ba/statuejpgaa03i3.webp 890w", "/static/5b52962d605c2ca5582ca608d2bd6c62/a9a89/statuejpgaa03i3.webp 1024w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/5b52962d605c2ca5582ca608d2bd6c62/853b2/statuejpgaa03i3.jpg 223w", "/static/5b52962d605c2ca5582ca608d2bd6c62/0a4fe/statuejpgaa03i3.jpg 445w", "/static/5b52962d605c2ca5582ca608d2bd6c62/7f80b/statuejpgaa03i3.jpg 890w", "/static/5b52962d605c2ca5582ca608d2bd6c62/72e01/statuejpgaa03i3.jpg 1024w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/jpeg"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/5b52962d605c2ca5582ca608d2bd6c62/7f80b/statuejpgaa03i3.jpg",
              "alt": "statuejpgaa03i3",
              "title": "statuejpgaa03i3",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span></p>
    <p>{`Here's what you do instead: Accept data as props. Only load in `}<inlineCode parentName="p">{`componentWillMount`}</inlineCode>{` if no data was given.`}</p>
    <p>{`Like this 👇`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`class App extends Component {
  constructor(props) {
    super(props);

    this.state = {
      data: (props.data || []).map(this.rowParse),
    };
  }

  dateParse = d3.timeParse("%d %b %Y");

  rowParse = ({ date, time, runner }) => ({
    date: this.dateParse(date),
    time: time
      .split(":")
      .map(Number)
      .reverse()
      .reduce((t, n, i) => (i > 0 ? t + n * 60 ** i : n)),
    runner,
  });

  componentWillMount() {
    if (!this.state.data.length) {
      d3.csv(
        "https://raw.githubusercontent.com/Swizec/server-side-d3-poc/master/src/data.csv"
      )
        .row(this.rowParse)
        .get((data) => this.setState({ data }));
    }
  }

  // render stuff
}
`}</code></pre>
    <p>{`In the `}<inlineCode parentName="p">{`constructor`}</inlineCode>{`, we copy data from props into `}<inlineCode parentName="p">{`state`}</inlineCode>{`. That's because components that load their own data usually keep it in state and putting it there means fewer changes to the rest of your code.`}</p>
    <p><inlineCode parentName="p">{`rowParse`}</inlineCode>{` is a helper method that turns individual rows from CVS strings into correct data types: dates for `}<inlineCode parentName="p">{`date`}</inlineCode>{`, seconds for `}<inlineCode parentName="p">{`time`}</inlineCode>{` to win the marathon, and `}<inlineCode parentName="p">{`runner`}</inlineCode>{` stays a string.`}</p>
    <p>{`In `}<inlineCode parentName="p">{`componentWillMount`}</inlineCode>{`, we now check if data is already present. If it isn't, we load it and everything works the same as it always has.`}</p>
    <h2 {...{
      "id": "hydrate-after-data-loads"
    }}>{`Hydrate `}<em parentName="h2">{`after`}</em>{` data loads`}</h2>
    <p>{`The final piece of the puzzle is hydrating your app `}<em parentName="p">{`after`}</em>{` your data is done loading. You're already showing a chart, so there's no need to be hasty and `}<inlineCode parentName="p">{`ReactDOM.hydrate`}</inlineCode>{` as soon as your JavaScript loads.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`No, once you hydrate, you hydrate. That said you can wait before hydrating.`}</p>{`— Dan (@dan_abramov) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/dan_abramov/status/913353504856723458"
      }}>{`September 28, 2017`}</a></blockquote>
    <p>{`You can't detect that your component already had children and avoid replacing them until you're ready. Instead, you can wait to hydrate in the first place.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`d3.csv("https://raw.githubusercontent.com/Swizec/server-side-d3-poc/master/src/data.csv")
  .row(this.rowParse)
  .get(data =>
      ReactDOM.hydrate(<app data={data}>, document.getElementById('root'))
  );
</app>
`}</code></pre>
    <p>{`And you have successfully solved the Flash of Doom seen in most D3 charts.`}</p>
    <p>{`\\`}{`o/`}</p>
    <h2 {...{
      "id": "caveat"
    }}>{`Caveat`}</h2>
    <p>{`Now you need a server. No more static page serverless webapp for your beautiful chart. No gh-pages and simple hosting…`}</p>
    <p>{`You `}<em parentName="p">{`could`}</em>{` use now.sh, and that's nice, but I don't know how to make it run my server. See 👉 `}<a parentName="p" {...{
        "href": "https://server-side-d3-poc-mamdozxwze.now.sh"
      }}>{`https://server-side-d3-poc-mamdozxwze.now.sh`}</a></p>
    <p>{`The panacea is to figure out how to tweak the create-react-app build process to insert your server-side rendered `}<inlineCode parentName="p">{`<App>`}</inlineCode>{` right into its `}<inlineCode parentName="p">{`index.html`}</inlineCode>{`. That's what I really want.`}</p>
    <p>{`I wonder if they'd accept a PR for that 🤔`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      